/**
 * 캠페인 화면이 「이 글을 숫자에 넣을 것인가」·「이 줄을 합계에 넣을 것인가」를 가리는 규칙.
 * 화면(`sections/campaign`)과 서버(MCP 시딩 요약)가 같은 함수를 쓴다.
 *
 * ⚠️ **강제추가 포스팅도 여기를 거친다.** 그것은 ES 조회를 안 거치고 바로 병합되므로,
 * 여기서 다시 판정하지 않으면 토글을 켜도 안 빠진다.
 *
 * ⚠️ `getPostMergeKey` 는 **세 겹으로 떨어진다** — id → link → (채널·계정·날짜·제목·본문).
 * 같은 글이 자동 수집과 강제추가로 두 번 들어올 수 있어서, 둘을 한 건으로 세려면 키가 필요하다.
 * 마지막 폴백까지 간다는 것은 id 도 link 도 없다는 뜻이라 **서로 다른 글이 뭉칠 수 있다** —
 * 그래도 안 뭉치는 것보다는 낫다고 본 자리다.
 */

import type { CampaignPost, CampaignDisplayToggles } from 'src/types/campaign/campaign-domain';

import { postInPeriod } from 'src/utils/campaign/campaign-period-filter';

function passesRepostQuoteFilters(
  post: CampaignPost | null | undefined,
  excludeXRepost?: boolean,
  excludeXQuote?: boolean,
) {
  if (excludeXRepost && post?.isRepost === true) return false;
  if (excludeXQuote && post?.isQuote === true) return false;
  return true;
}

function passesInstagramStoryFilter(post: CampaignPost | null | undefined, includeInstagramStory?: boolean) {
  if (includeInstagramStory) return true;
  return post?.isStory !== true;
}

export function passesCampaignDisplayPostFilters(
  post: CampaignPost | null | undefined,
  { excludeDeleted, excludeXRepost, excludeXQuote, includeInstagramStory, periodFilter }: CampaignDisplayToggles,
) {
  // 개별 추가(forcedAdditions) 포스트는 base 필터를 거치지 않고 병합되므로 여기서도 판정한다
  if (excludeDeleted && post?.isDeleted === true) return false;
  if (!passesRepostQuoteFilters(post, excludeXRepost, excludeXQuote)) return false;
  if (!passesInstagramStoryFilter(post, includeInstagramStory)) return false;
  if (!postInPeriod(post, periodFilter)) return false;
  return true;
}

export function getPostMergeKey(post: CampaignPost | null | undefined) {
  const id = String(post?.id || '').trim();
  if (id) return `id:${id}`;
  const link = String(post?.link || '').trim();
  if (link) return `link:${link}`;
  const source = String(post?.source || post?.platform || '').trim().toLowerCase();
  const account = String(post?.account || post?.username || '').trim().toLowerCase();
  const postDate = String(post?.postDate || '').trim();
  const title = String(post?.title || '').trim();
  const content = String(post?.content || '').trim();
  return `fallback:${source}|${account}|${postDate}|${title}|${content}`;
}

/**
 * campaign-list SortableRow 과 동일: 테이블에 포스팅·조회·회수율 숫자가 보일 때만 메트릭 합산에 포함.
 */
export function shouldIncludeCampaignMetricsInListTable({
  campaign,
  listCampaignLoading,
  campaignPostsLoadingMap,
  isFullCampaignDataLoaded,
  hasForcedAdditionLinks = false,
  isForcedAdditionPostsLoading = false,
}: {
  campaign: { id?: unknown; status?: unknown } | null | undefined;
  listCampaignLoading?: boolean;
  campaignPostsLoadingMap?: Record<string, boolean> | null;
  isFullCampaignDataLoaded?: boolean;
  hasForcedAdditionLinks?: boolean;
  isForcedAdditionPostsLoading?: boolean;
}) {
  const id = String(campaign?.id ?? '').trim();
  if (!id) return false;
  const map = typeof campaignPostsLoadingMap === 'object' && campaignPostsLoadingMap !== null
    ? campaignPostsLoadingMap
    : {};
  const rowStatus = map[id];
  const forcedRowLoading = hasForcedAdditionLinks && isForcedAdditionPostsLoading;
  const rowLoading = listCampaignLoading || rowStatus === true || forcedRowLoading;
  if (rowLoading) return false;
  const isEndedCampaign = campaign?.status === '종료';
  const isCampaignDataLoaded = rowStatus === false;
  const showDashForEnded =
    isEndedCampaign && !isFullCampaignDataLoaded && !isCampaignDataLoaded;
  if (showDashForEnded) return false;
  return true;
}
